import type { TraceEvent } from '../types';

export type JsonObject = Record<string, unknown>;

export interface ParsedToolCall {
  id?: string;
  name: string;
  arguments?: unknown;
  raw: unknown;
}

export interface ParsedContentBlock {
  type: string;
  text?: string;
  [key: string]: unknown;
}

export type ParsedTracePayload =
  | {
      kind: 'assistant_tool_calls';
      toolCalls: ParsedToolCall[];
      text?: string;
      raw: unknown;
    }
  | { kind: 'assistant_text'; text: string; raw: unknown }
  | {
      kind: 'tool_call';
      toolName: string;
      toolCallId?: string;
      input?: unknown;
      output?: unknown;
      error?: string;
      raw: unknown;
    }
  | {
      kind: 'tool_result_message';
      toolCallId?: string;
      content?: unknown;
      paired: boolean;
      raw: unknown;
    }
  | {
      kind: 'user_message';
      content?: unknown;
      runtimeContext?: string[];
      text?: string;
      raw: unknown;
    }
  | { kind: 'runtime_guidance'; blocks: string[]; raw: unknown }
  | { kind: 'billing_router'; route?: string; raw: unknown }
  | {
      kind: 'llm_call';
      provider?: string;
      model?: string;
      finishReason?: string;
      raw: unknown;
    }
  | { kind: 'generic_json'; value: unknown; raw: unknown }
  | { kind: 'generic_text'; text: string; raw: unknown };

export function parseTracePayload(event: TraceEvent): ParsedTracePayload {
  const raw = parseEventPayload(event);
  const details = event.details || {};

  if (isBillingRouter(raw)) {
    return {
      kind: 'billing_router',
      route: stringValue(raw.route),
      raw,
    };
  }

  const runtimeBlocks = extractRuntimeGuidance(raw);
  if (runtimeBlocks.length > 0 && event.kind !== 'user_message') {
    return { kind: 'runtime_guidance', blocks: runtimeBlocks, raw };
  }

  if (event.kind === 'assistant_message') {
    const rawCandidates = parseEventPayloadCandidates(event);
    for (const candidate of rawCandidates) {
      const toolResult = parseToolResultMessage(candidate, details);
      if (toolResult) return toolResult;

      const toolCalls = parseAssistantToolCalls(candidate);
      if (toolCalls.length > 0) {
        const record = objectValue(candidate);
        return {
          kind: 'assistant_tool_calls',
          toolCalls,
          text: textFromContent(record?.content ?? record?.text),
          raw: candidate,
        };
      }
    }
    const text =
      textFromContent(objectValue(raw)?.content ?? objectValue(raw)?.text) ||
      fallbackEventText(event);
    return text
      ? { kind: 'assistant_text', text, raw }
      : { kind: 'generic_json', value: raw, raw };
  }

  if (event.kind === 'user_message') {
    const text = textFromContent(raw) || fallbackEventText(event);
    return {
      kind: 'user_message',
      content: raw,
      runtimeContext: runtimeBlocks,
      text,
      raw,
    };
  }

  if (event.kind === 'tool_call') {
    const payload = objectValue(raw);
    const toolName = stringValue(details.tool_name) || event.title || 'Tool';
    const input =
      parseJSONLike(payload?.input) ??
      parseJSONLike(payload?.input_content) ??
      parseJSONLike(payload?.arguments) ??
      raw;
    const output =
      parseJSONLike(payload?.output) ?? parseJSONLike(payload?.output_content);
    const error =
      stringValue(details.error_message) ||
      stringValue(details.error_class) ||
      (event.status !== 'success' ? fallbackEventText(event) : undefined);
    return {
      kind: 'tool_call',
      toolName,
      toolCallId: stringValue(details.tool_call_id),
      input,
      output,
      error,
      raw,
    };
  }

  if (event.kind === 'llm_call') {
    return {
      kind: 'llm_call',
      provider: stringValue(details.provider),
      model: stringValue(details.model),
      finishReason: stringValue(details.finish_reason),
      raw,
    };
  }

  if (raw && typeof raw === 'object') {
    return { kind: 'generic_json', value: raw, raw };
  }

  return { kind: 'generic_text', text: fallbackEventText(event), raw };
}

export function fallbackEventText(event: TraceEvent) {
  const raw = parseEventPayload(event);
  return extractTraceText(raw) || extractTraceText(event.summary) || '';
}

function parseEventPayload(event: TraceEvent): unknown {
  return parseEventPayloadCandidates(event)[0] ?? '';
}

function parseEventPayloadCandidates(event: TraceEvent): unknown[] {
  const candidates = [event.payload, event.summary].filter(
    value => value !== undefined && value !== null && value !== ''
  );
  return candidates.map(candidate => parseJSONLike(candidate));
}

export function parseJSONLike(value: unknown): unknown {
  return deepParseJSONLike(value, 0);
}

function deepParseJSONLike(value: unknown, depth: number): unknown {
  if (depth > 16) return value;
  if (typeof value !== 'string') {
    if (Array.isArray(value)) {
      return value.map(item => deepParseJSONLike(item, depth + 1));
    }
    const record = objectValue(value);
    if (!record) return value;
    return Object.fromEntries(
      Object.entries(record).map(([key, nested]) => [
        key,
        deepParseJSONLike(nested, depth + 1),
      ])
    );
  }

  const trimmed = value.trim();
  if (!trimmed) return '';
  const parsed = parseJSON(trimmed);
  return parsed.ok ? deepParseJSONLike(parsed.value, depth + 1) : value;
}

function parseJSON(
  value: string
): { ok: true; value: unknown } | { ok: false } {
  if (
    !value.startsWith('{') &&
    !value.startsWith('[') &&
    !value.startsWith('"')
  ) {
    return { ok: false };
  }
  try {
    return { ok: true, value: JSON.parse(value) };
  } catch {
    return { ok: false };
  }
}

function parseAssistantToolCalls(raw: unknown): ParsedToolCall[] {
  const record = objectValue(raw);
  const rawCalls = record?.tool_calls ?? record?.toolCalls;
  const calls = Array.isArray(rawCalls) ? rawCalls : parseJSONLike(rawCalls);
  if (!Array.isArray(calls)) return [];
  return calls.flatMap(call => {
    const recordCall = objectValue(call);
    const fn = objectValue(recordCall?.function);
    const name = stringValue(fn?.name) || stringValue(recordCall?.name);
    if (!name) return [];
    return [
      {
        id: stringValue(recordCall?.id),
        name,
        arguments: parseJSONLike(fn?.arguments ?? recordCall?.arguments),
        raw: call,
      },
    ];
  });
}

function parseToolResultMessage(
  raw: unknown,
  details: Record<string, unknown>
): ParsedTracePayload | null {
  const record = objectValue(raw);
  const role = stringValue(record?.role) || stringValue(details.role);
  if (role !== 'tool') return null;
  return {
    kind: 'tool_result_message',
    toolCallId:
      stringValue(record?.tool_call_id) ||
      stringValue(record?.toolCallId) ||
      stringValue(details.tool_call_id),
    content: parseJSONLike(record?.content),
    paired: Boolean(
      record?.tool_call_id || record?.toolCallId || details.tool_call_id
    ),
    raw,
  };
}

function isBillingRouter(raw: unknown): raw is JsonObject {
  const record = objectValue(raw);
  return record?.call_kind === 'tool_router';
}

function extractRuntimeGuidance(raw: unknown): string[] {
  const text = extractTraceText(raw);
  const blocks: string[] = [];
  for (const pattern of [
    /<system-reminder>([\s\S]*?)<\/system-reminder>/gi,
    /<heartbeat-run>([\s\S]*?)<\/heartbeat-run>/gi,
  ]) {
    for (const match of text.matchAll(pattern)) {
      const block = match[0]?.trim();
      if (block) blocks.push(block);
    }
  }
  return blocks;
}

function textFromContent(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    return value.map(textFromContent).filter(Boolean).join('\n\n');
  }
  const record = objectValue(value);
  if (!record) return '';
  return textFromContent(record.text ?? record.content ?? record.message);
}

function extractTraceText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    return value.map(extractTraceText).filter(Boolean).join('\n\n');
  }
  const record = objectValue(value);
  if (!record) return '';
  for (const key of ['text', 'content', 'message', 'error']) {
    const extracted = extractTraceText(record[key]);
    if (extracted) return extracted;
  }
  return '';
}

function objectValue(value: unknown): JsonObject | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as JsonObject;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}
